import React from 'react';
import { Controller, Control } from 'react-hook-form';
import TextField, { BaseTextFieldProps } from '@mui/material/TextField';
import {
  Box,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Icon } from '@components/icon';
import { TIconName } from '@components/icon/types/icon-name';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

interface Props extends BaseTextFieldProps {
  name: string;
  control: Control<any>;
  label: string;
  iconName?: TIconName;
  sx?: any;
  type?: 'text' | 'password';
}

export function Input({
  name,
  control,
  label,
  iconName,
  sx,
  type,
  ...props
}: Props) {
  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: any) => {
    event.preventDefault();
  };

  const getType = () => {
    if (type === 'password') {
      return showPassword ? 'text' : 'password';
    }
    return type || 'text';
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Grid sx={sx} container>
          {iconName ? (
            <Grid container>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'background.default',
                  color: 'primary.main',
                  zIndex: 1,
                  ml: 2,
                  pl: 1.2,
                  pr: 1.5,
                  mb: -0.8,
                }}
              >
                <Icon name={iconName} alt="icon" />
                <FormLabel
                  component="legend"
                  sx={{
                    fontFamily: 'Mukta',
                    fontSize: 19,
                    color: 'primary.main',
                    fontWeight: '500',
                    ml: 0.5,
                  }}
                >
                  {label}
                </FormLabel>
              </Box>
            </Grid>
          ) : (
            <FormLabel
              component="legend"
              sx={{
                fontFamily: 'Mukta',
                fontSize: 19,
                color: 'text.primary',
                fontWeight: '500',
                mb: 0.8,
              }}
            >
              {label}
            </FormLabel>
          )}
          <TextField
            helperText={fieldState.error ? fieldState.error.message : null}
            error={!!fieldState.error}
            onChange={field.onChange}
            value={field.value}
            type={getType()}
            InputProps={
              type === 'password'
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'text.primary' },
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#ffffff',
                '& fieldset': {
                  borderColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.16)'
                      : 'rgba(0, 0, 0, 0.23)',
                  transition: 'border-color 0.2s ease-in-out',
                },
                '&:hover fieldset': {
                  borderColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.36)'
                      : 'rgba(0, 0, 0, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: 'text.primary',
                '&::placeholder': {
                  color: 'text.secondary',
                  opacity: 0.7,
                },
              },
            }}
            {...props}
          />
        </Grid>
      )}
    />
  );
}
